import LinkButton from '@carvana/ds-link-button';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import ModalContext from '~/context/srp/ModalContext';
import getLongMonthDayYear from '~/utilities/date/getLongMonthDayYear';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import optimizeImage, { type Options } from '~/utilities/optimizeImage';
import { PromoBanners } from './constants';
import { useGetGeicoDiscount, useSendViewedBannerEvent } from './hooks';
import { getBannerName } from './utils';

const bannerName = getBannerName(PromoBanners.Geico);

const OPTIMIZATION_CONFIG: Options = {
  format: 'auto',
  optimize: 'low',
  quality: 90
};

export default function GeicoBanner() {
  const { setGeicoDiscountModalOpen } = useContext(ModalContext);
  const discount = useGetGeicoDiscount();
  const sendViewedBannerEvent = useSendViewedBannerEvent();

  const handleSeeDetails = useCallback(() => {
    if (discount) setGeicoDiscountModalOpen(true, discount);
  }, [discount, setGeicoDiscountModalOpen]);

  useEffect(() => {
    sendViewedBannerEvent(bannerName);
  }, [sendViewedBannerEvent]);

  const expirationDate = useMemo(
    () =>
      discount?.expirationDateTime
        ? getLongMonthDayYear(discount.expirationDateTime)
        : null,
    [discount?.expirationDateTime]
  );

  if (!discount) return null;

  return (
    <div
      className="relative mb-2xl grid grid-cols-[1fr_auto] overflow-hidden rounded-md bg-background-muted-subtle sm:mb-3xl lg:rounded-xl"
      data-testid="geico-promo-banner"
    >
      <div className="relative z-1 flex min-w-0 flex-col gap-lg py-2xl pl-2xl md:py-4xl md:pl-4xl">
        <img
          src={`${appAssetsCdnUrl}/svgs/geico.svg`}
          alt="GEICO"
          className="w-[75px] sm:w-[100px]"
        />

        <div className="flex flex-col gap-md text-text-strong">
          <p
            role="heading"
            className="t-heading-sm sm:t-heading-xl md:t-heading-2xl"
          >
            Your exclusive{' '}
            <span data-testid="geico-banner-amount">
              ${formatAndDropDecimal(discount.amount)}
            </span>{' '}
            discount is applied
          </p>
          {expirationDate && (
            <p className="t-body-sm">Expires {expirationDate}</p>
          )}
        </div>

        <LinkButton
          variant="default"
          size="sm"
          underline={false}
          className="text-left"
          data-testid="geico-banner-see-details"
          data-adobe-id={null}
          data-analytics-id="Banner - See Discount Details"
          data-analytics-attributes={JSON.stringify({
            category: 'GEICO Discount',
            label: 'See Discount Details'
          })}
          onClick={handleSeeDetails}
        >
          See Discount Details
        </LinkButton>
      </div>

      <div className="relative w-[118px] sm:w-[352px] md:w-[328px] lg:w-[328px] xl:w-[518px]">
        <div className="absolute h-full w-full">
          <picture>
            <source
              media={`(min-width: ${breakpoints.xl}px)`}
              srcSet={optimizeImage(
                `${appAssetsCdnUrl}/images/helios/promoBanners/geico/desktop-large.png`,
                OPTIMIZATION_CONFIG
              )}
            />
            <source
              media={`(min-width: ${breakpoints.md}px)`}
              srcSet={optimizeImage(
                `${appAssetsCdnUrl}/images/helios/promoBanners/geico/desktop-small.png`,
                OPTIMIZATION_CONFIG
              )}
            />
            <source
              media={`(min-width: ${breakpoints.sm}px)`}
              srcSet={optimizeImage(
                `${appAssetsCdnUrl}/images/helios/promoBanners/geico/tablet.png`,
                OPTIMIZATION_CONFIG
              )}
            />
            <img
              alt="GEICO discount promotion"
              src={optimizeImage(
                `${appAssetsCdnUrl}/images/helios/promoBanners/geico/mobile.png`,
                OPTIMIZATION_CONFIG
              )}
              className="h-full w-full object-cover object-left"
            />
          </picture>
        </div>
      </div>
    </div>
  );
}
